import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  useGamesGridConfig,
  useGetCombinedGames,
  useGetFavoritesGames,
  useInfiniteScroll,
  useRecentSearches,
  useSearch
} from 'react-easyrocket';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { useGameCardSize } from 'src/hooks/useGameCardSize';
import { useNavigateTo404 } from 'src/hooks/useNavigateTo404';
import { GamesGrid } from '../GamesGrid';
import { GamesAndProvidersSearch } from './components/GamesAndProvidersSearch';
import { GamesCategoriesSwitcher } from './components/GamesCategoriesSwitcher';
import { GAMES_SEARCH_SYMBOLS_THRESOLD } from './constants';
import { useGetProviderFromUrl } from './hooks/useGetProviderFromUrl';
import { useGetCategoryParams } from './hooks/useGetCategoryParams';

interface GamesControlPanelProps {
  target?: string;
  image?: string;
}

const _GamesControlPanel = ({ image, target }: GamesControlPanelProps) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const pageRef = useRef<HTMLDivElement>(null);
  const { saveRecentResult } = useRecentSearches({
    storageKey: 'gameRecentSearches'
  });

  const isGamesOrProviderPage =
    location.pathname.includes('games') || location.pathname.includes('provider');
  const { value, onChange, debouncedValue, reset } = useSearch();
  const { totalGames, columns } = useGamesGridConfig();

  const { providerId } = useGetProviderFromUrl();
  const { categoryId } = useGetCategoryParams();

  const isFavoriteCategory = categoryId === 'favorites';
  const { games: favoriteGames } = useGetFavoritesGames({}, { enabled: isFavoriteCategory });
  const isDebouncedValueLength = debouncedValue?.length >= GAMES_SEARCH_SYMBOLS_THRESOLD;

  const enableGetGamesRequest = useMemo(() => {
    if (!isGamesOrProviderPage) {
      return isDebouncedValueLength;
    }
    return true;
  }, [isDebouncedValueLength, isGamesOrProviderPage]);

  useNavigateTo404(isGamesOrProviderPage && !enableGetGamesRequest);

  const { games, fetchNextPage, hasNextPage, isFetching, isFetched } = useGetCombinedGames(
    {
      name: isDebouncedValueLength ? debouncedValue : undefined,
      per_page: totalGames,
      provider_id: !debouncedValue ? providerId : undefined
    },
    {
      categoryId,
      target: target || categoryId,
      enabled: enableGetGamesRequest,
      onlyAllGamesSearch: Boolean(debouncedValue)
    }
  );

  const { parentRef, childRef } = useInfiniteScroll({
    callback: () => {
      if (hasNextPage) {
        fetchNextPage();
      }
    }
  });
  const gameCardSize = useGameCardSize(parentRef.current?.clientWidth, columns);
  const gamesToShow = useMemo(() => {
    if (isDebouncedValueLength) return games;
    if (isFavoriteCategory) return favoriteGames;
    return games;
  }, [categoryId, favoriteGames, games]);

  useEffect(() => {
    if (isGamesOrProviderPage) {
      setTimeout(() => pageRef?.current?.scrollIntoView(true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGamesOrProviderPage, pathname]);

  const handleGamePlay = useCallback(() => {
    saveRecentResult(debouncedValue, Boolean(games?.length));
  }, [debouncedValue, games, saveRecentResult]);

  const searchResults = (
    <div className="flex flex-col justify-center items-center mt-[30px] w-full" ref={parentRef}>
      <GamesGrid
        onGameClick={handleGamePlay}
        games={gamesToShow}
        isFetching={isFetching}
        isFetched={isFetched}
        gameCardProps={gameCardSize}
        StubComponent={
          <div className="w-full mt-[30px] flex flex-col justify-center items-center">
            <p className="text-gp_text text-center font-bold w-[70%] text-sm">
              {t('gamesPage.noGamesInCategory')}
            </p>
            <img src={image} className="md:w-[50%] w-auto" alt="no games" />
          </div>
        }
      />
      {!isFetching && hasNextPage && <div className="h-[20px] w-full" ref={childRef} />}
    </div>
  );

  return (
    <>
      <GamesCategoriesSwitcher
        lightActiveLink={!debouncedValue && !providerId}
        onCategoryClick={reset}
      />
      <div className="w-full">
        <GamesAndProvidersSearch onChange={onChange} value={value} />
      </div>
      {isGamesOrProviderPage && searchResults}
      {!isGamesOrProviderPage && debouncedValue.length >= 2 && searchResults}
    </>
  );
};

export const GamesControlPanel = memo(_GamesControlPanel);
